import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'ramda';
import { ApplicationState } from '../store';
import { fetchRequest as fetchProcessor } from '../store/processor/actions';
import { fetchRequest as fetchReceipt } from '../store/receipt/actions';
import { fetchRequest as fetchEmv } from '../store/emv/actions';
import { fetchRequest as fetchHashedHotCard } from '../store/hashedHotCard/actions';
import { fetchRequest as fetchCard } from '../store/card/actions';
import { findMany as fetchCardPrompts } from '../store/cardPrompt/cardPromptSlice';
import { fetchRequest as fetchCloseLoop } from '../store/closeLoop/actions';
import { fetchRequest as fetchOfflinePaymentTypeGroup } from '../store/offlinePaymentTypeGroup/actions';
import { fetchRequest as fetchTokenWallet } from '../store/tokenWallet/actions';
import { fetchRequest as fetchMcc } from '../store/mcc/actions';
import { fetchRequest as fetchAllLogos } from '../store/terminalBackground/actions';
import { fetchRequest as fetchCustomGroups } from '../store/customGroup/actions';
import { fetchRequest as fetchTerminalApp, fetchVersion } from '../store/myAxis/actions';

interface DataFetchingState {
  isLoading: boolean;
  isComplete: boolean;
  error: string | null;
}

interface IndividualLoadingStates {
  emv: boolean;
  closeLoop: boolean;
  offlinePaymentTypeGroup: boolean;
  tokenWallet: boolean;
  mcc: boolean;
  hashedHotCard: boolean;
  card: boolean;
  cardPrompt: boolean;
  processor: boolean;
  receipt: boolean;
  logo: boolean;
  customGroups: boolean;
  terminalApp: boolean;
}

export default function useDataFetching() {
  const dispatch = useDispatch();
  const [fetchingState, setFetchingState] = useState<DataFetchingState>({
    isLoading: true,
    isComplete: false,
    error: null
  });
  
  // Individual loading states for each data source
  const [individualLoading, setIndividualLoading] = useState<IndividualLoadingStates>({
    emv: true,
    closeLoop: true,
    offlinePaymentTypeGroup: true,
    tokenWallet: true,
    mcc: true,
    hashedHotCard: true,
    card: true,
    cardPrompt: true,
    processor: true,
    receipt: true,
    logo: true,
    customGroups: true,
    terminalApp: true,
  });
  
  // Use ref to prevent multiple data fetching
  const hasInitialized = useRef(false);

  // Get all the data from Redux state
  const emvOptions = useSelector((state: ApplicationState) => state.emv.data) ?? [];
  const closeLoopOptions = useSelector((state: ApplicationState) => state.closeloop.data) ?? [];
  const offlinePaymentTypeGroup = useSelector((state: ApplicationState) => state.offlinePaymentTypeGroup.data) ?? [];
  const tokenWalletOptions = useSelector((state: ApplicationState) => state.tokenWallet.data) ?? [];
  const mccOptions = useSelector((state: ApplicationState) => state.mcc.data) ?? [];
  const hashedHotCardOptions = useSelector((state: ApplicationState) => state.hashedHotCard.data ?? []);
  const cardData = useSelector((state: ApplicationState) => state.card?.data?.data);
  const cardPromptData = useSelector((state: ApplicationState) => state.cardPrompt?.entries);
  const processorData = useSelector((state: ApplicationState) => state.processor?.data);
  const receiptData = useSelector((state: ApplicationState) => state.receipt?.data);
  const logoData = useSelector((state: ApplicationState) => state.terminalBackground?.data);
  const customGroupsData = useSelector((state: ApplicationState) => state.customGroup?.data);
  const terminalAppData = useSelector((state: ApplicationState) => state.myAxis?.applications);
  const firmwareData = useSelector((state: ApplicationState) => state.myAxis?.firmwares);

  // Update individual loading states based on data availability
  useEffect(() => {
    setIndividualLoading(prev => ({
      ...prev,
      emv: emvOptions === undefined || emvOptions === null,
      closeLoop: closeLoopOptions === undefined || closeLoopOptions === null,
      offlinePaymentTypeGroup: offlinePaymentTypeGroup === undefined || offlinePaymentTypeGroup === null,
      tokenWallet: tokenWalletOptions === undefined || tokenWalletOptions === null,
      mcc: mccOptions === undefined || mccOptions === null,
      hashedHotCard: hashedHotCardOptions === undefined || hashedHotCardOptions === null,
      card: cardData === undefined || cardData === null,
      cardPrompt: cardPromptData === undefined || cardPromptData === null,
      processor: processorData === undefined || processorData === null,
      receipt: receiptData === undefined || receiptData === null,
      logo: logoData === undefined || logoData === null,
      customGroups: customGroupsData === undefined || customGroupsData === null,
      terminalApp: terminalAppData === undefined || terminalAppData === null,
    }));
  }, [
    emvOptions, closeLoopOptions, offlinePaymentTypeGroup, tokenWalletOptions,
    mccOptions, hashedHotCardOptions, cardData, cardPromptData, processorData,
    receiptData, logoData, customGroupsData, terminalAppData
  ]);

  // Check if all required data is loaded
  const checkDataCompleteness = useCallback(() => {
    const requiredData = [
      { data: emvOptions, name: 'EMV Options' },
      { data: closeLoopOptions, name: 'Close Loop Options' },
      { data: offlinePaymentTypeGroup, name: 'Offline Payment Type Group' },
      { data: tokenWalletOptions, name: 'Token Wallet Options' },
      { data: mccOptions, name: 'MCC Options' },
      { data: hashedHotCardOptions, name: 'Hashed Hot Card Options' },
      { data: cardData, name: 'Card Data' },
      { data: cardPromptData, name: 'Card Prompt Data' },
      { data: processorData, name: 'Processor Data' },
      { data: receiptData, name: 'Receipt Data' },
      { data: logoData, name: 'Logo Data' },
      { data: customGroupsData, name: 'Custom Groups Data' },
      { data: terminalAppData, name: 'Terminal App Data' }
    ];

    const incompleteData = requiredData.filter(({ data, name }) => {
      // Data is incomplete only if it's undefined or null
      // Empty arrays, empty objects, or empty strings are considered valid data
      if (data === undefined || data === null) {
        console.log(`Data not loaded: ${name}`);
        return true;
      }
      return false;
    });

    return incompleteData.length === 0;
  }, [
    emvOptions, closeLoopOptions, offlinePaymentTypeGroup, tokenWalletOptions,
    mccOptions, hashedHotCardOptions, cardData, cardPromptData, processorData,
    receiptData, logoData, customGroupsData, terminalAppData
  ]);

  // Initial data fetching effect - only run once
  useEffect(() => {
    if (hasInitialized.current) {
      return; // Already initialized
    }
    
    hasInitialized.current = true;
    
    // Fetch all required data
    dispatch(fetchProcessor(null));
    dispatch(fetchReceipt(null));
    dispatch(fetchEmv());
    dispatch(fetchCloseLoop());
    dispatch(fetchOfflinePaymentTypeGroup());
    dispatch(fetchHashedHotCard());
    dispatch(fetchTokenWallet());
    dispatch(fetchMcc());
    dispatch(fetchCard(null));
    dispatch(fetchCardPrompts());
    dispatch(fetchAllLogos(null, {isList: true}));
    dispatch(fetchCustomGroups(null));
    
    // Fetch terminal app and versions
    dispatch(
      fetchTerminalApp((applications) => {
        if (Array.isArray(applications)) {
          applications.map((appName: string) => {
            dispatch(fetchVersion(appName));
          });
        }
      })
    );
  }, []); // Add startLoading to dependency array

  // Effect to monitor data loading state
  useEffect(() => {
    const isComplete = checkDataCompleteness();
    
    if (isComplete && !fetchingState.isComplete) {
      setFetchingState(prev => ({
        ...prev,
        isLoading: false,
        isComplete: true
      }));
    }
  }, [
    checkDataCompleteness,
    fetchingState.isComplete
  ]);

  return {
    ...fetchingState,
    // Individual loading states
    individualLoading,
    // Data getters
    emvOptions,
    closeLoopOptions,
    offlinePaymentTypeGroup,
    tokenWalletOptions,
    mccOptions,
    hashedHotCardOptions,
    cardData,
    cardPromptData,
    processorData,
    receiptData,
    logoData,
    customGroupsData,
    terminalAppData,
    firmwareData
  };
}
